import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Segmented } from 'antd';

import { CHEVRON_DOWN } from '../icons';
import { fitEntries, formatListCount, spokenCount } from '../../utils/relatedListSwitcher';

// The space between the segmented control and the More button.
const GAP = 8;
// Dropdown overflow: no flip, shift into the viewport with 8px to spare.
const SHIFT_INTO_VIEW = { adjustX: false, adjustY: true, shiftX: 8 };

/** One entry as the segment (or the More menu row) draws it: the name, then its count. */
function EntryLabel({ entry }) {
	const count = formatListCount(entry.count);
	return (
		<span className='related-switcher__entry' title={entry.name}>
			<span className='related-switcher__name'>{entry.name}</span>
			{count ? (
				<>
					<span className='related-switcher__count' aria-hidden='true'>{count}</span>
					<span className='related-switcher__spoken'>, {spokenCount(entry.count)}</span>
				</>
			) : null}
		</span>
	);
}

const sameNumbers = (a, b) => a.length === b.length && a.every((value, index) => value === b[index]);

/**
 * The Related items section's switcher (KPR-719; docs/platform-standards.md
 * §13 and §5): the entries of the section (Related items, then each related
 * list), each with its count, as the neutral `Segmented`. It is not a second
 * tab bar: it switches what one section shows.
 *
 *   <RelatedListSwitcher
 *     entries={[{ id, name, count }]}   // count: a number, or null/undefined while unknown
 *     value={selectedId}
 *     onChange={setSelectedId}
 *   />
 *
 * As many entries as fit the width are drawn; the rest are in a More menu
 * whose rows carry counts too. The selected entry is always drawn: one chosen
 * from More takes a place in the control, and goes back when another is
 * chosen. The fit follows the width of the switcher itself (a pane resized, a
 * phone rotated), measured from the entries' own widths in a hidden copy.
 *
 * The control is one radio group: arrow keys move between the drawn entries.
 * Each entry's name for a screen reader is "Name, 12 items".
 */
export default function RelatedListSwitcher({ entries, value, onChange, ariaLabel = 'Related lists' }) {
	const rootRef = useRef(null);
	const measureRef = useRef(null);
	const [metrics, setMetrics] = useState({ available: 0, widths: [], moreWidth: 0 });

	const ids = useMemo(() => entries.map(entry => entry.id), [entries]);
	// What changes a segment's width: ids, names and counts.
	const signature = useMemo(() => entries.map(entry => `${entry.id}\u0000${entry.name}\u0000${entry.count ?? ''}`).join('\u0001'), [entries]);

	const measure = useCallback(() => {
		const root = rootRef.current;
		const copy = measureRef.current;
		if (!root || !copy) return;
		const widths = [...copy.querySelectorAll('.ant-segmented-item')].map(node => Math.ceil(node.offsetWidth || 0));
		const more = copy.querySelector('.related-switcher__more');
		const next = {
			available: Math.floor(root.clientWidth || 0),
			widths,
			moreWidth: Math.ceil(more ? more.offsetWidth || 0 : 0),
		};
		setMetrics(prev => (prev.available === next.available && prev.moreWidth === next.moreWidth && sameNumbers(prev.widths, next.widths) ? prev : next));
	}, []);

	// Before paint, so the first frame is already the fitted one. Entries
	// changing (a count arrived) changes the widths, so it measures again.
	useLayoutEffect(() => { measure(); }, [measure, signature]);

	useLayoutEffect(() => {
		const root = rootRef.current;
		if (!root) return undefined;
		let observer = null;
		if (typeof ResizeObserver !== 'undefined') {
			observer = new ResizeObserver(() => measure());
			observer.observe(root);
		}
		// Rotating a phone or resizing the window moves the section even where
		// no observer exists; a second measure is harmless where both fire.
		window.addEventListener('resize', measure);
		return () => {
			if (observer) observer.disconnect();
			window.removeEventListener('resize', measure);
		};
	}, [measure]);

	const { visible, overflow } = useMemo(() => fitEntries({
		ids,
		selectedId: value,
		widths: metrics.widths,
		moreWidth: metrics.moreWidth,
		available: metrics.available,
		gap: GAP,
	}), [ids, value, metrics]);

	const byId = useMemo(() => new Map(entries.map(entry => [entry.id, entry])), [entries]);
	const options = visible.map(id => ({ value: id, label: <EntryLabel entry={byId.get(id)} /> }));
	const menuItems = overflow.map(id => ({
		key: id,
		label: <EntryLabel entry={byId.get(id)} />,
		// The menu row's own accessible name; the label's spoken count rides inside it.
	}));

	const moreButton = (
		<Button className='related-switcher__more' aria-haspopup='menu'>
			<span>More</span>
			<CHEVRON_DOWN aria-hidden='true' />
		</Button>
	);

	return (
		<div className='related-switcher' ref={rootRef}>
			<Segmented
				className='related-switcher__segments segmented--neutral'
				aria-label={ariaLabel}
				value={visible.includes(value) ? value : visible[0]}
				onChange={onChange}
				options={options}
			/>
			{overflow.length ? (
				<Dropdown
					trigger={['click']}
					// Under the button, shifted into view rather than flipped: the menu can be
					// wider than the room on either side of a phone's More button.
					placement='bottomLeft'
					autoAdjustOverflow={SHIFT_INTO_VIEW}
					overlayClassName='related-switcher__menu'
					menu={{ items: menuItems, selectedKeys: [], onClick: ({ key }) => onChange(key) }}
				>
					{moreButton}
				</Dropdown>
			) : null}
			{/* A hidden copy of every entry, for its width. It takes no room and
			    is out of the tab order and the accessibility tree. */}
			<div className='related-switcher__measure' ref={measureRef} aria-hidden='true'>
				<Segmented
					className='segmented--neutral'
					tabIndex={-1}
					value={ids[0]}
					options={entries.map(entry => ({ value: entry.id, label: <EntryLabel entry={entry} />, disabled: true }))}
				/>
				{moreButton}
			</div>
		</div>
	);
}
